/* eslint-disable react/no-children-prop */
import { createMaterialTopTabNavigator } from "@react-navigation/material-top-tabs";
import { DrawerTabBar } from "./TabBar";
import { NavigationContainer, NavigationIndependentTree } from "@react-navigation/native";
import { router, useGlobalSearchParams, useSegments } from "expo-router";
import { useTheme } from "@/hooks/useTheme";
import { PreviewHeader, type StatusColumn } from "@/components/screens/shared/PreviewHeader";
import { PreviewFooter } from "@/components/screens/shared/PreviewFooter";
import { FilesTab } from "@/components/screens/shared/FilesTab";
import { titleCase } from "@/lib/helpers";
import NewUser from "@/app/(app)/(root)/(users)/users/new";
import { ThemedText } from "@/components/ThemedText";
import NewProjectPage from "@/app/(app)/(root)/(projects)/projects/new";
import NewClient from "@/app/(app)/(root)/(clients)/clients/new";
import NewContact from "@/app/(app)/(root)/(contacts)/contacts/new";
import NewTimecard from "@/app/(app)/(root)/(time_cards)/time_cards/new";
import NewCoursePage from "@/app/(app)/(root)/(courses)/courses/new";
import { Comments } from "@/components/Comments";
import { useEntityData } from "@/hooks/useEntityData";
import { useEffect, useState, useRef, useMemo } from "react";
import useAuth from "@/hooks/useAuth";
import { clientService } from "@/services/clientService";
import { ClientType } from "@/types/db/clients";
import { UserEditProvider } from "@/providers/UserEditProvider";
import { ProjectEditProvider } from "@/providers/ProjectEditProvider";
import { ClientEditProvider } from "@/providers/ClientEditProvider";
import { TimecardEditProvider } from "@/providers/TimecardEditProvider";
import { ShiftEditProvider } from "@/providers/ShiftEditProvider";
import { ContactEditProvider } from "@/providers/ContactEditProvider";
import { InvoiceEditProvider } from "@/providers/InvoiceEditProvider";
import { EstimateEditProvider } from "@/providers/EstimateEditProvider";
import { CourseEditProvider } from "@/providers/CourseEditProvider";
import { IncidentEditProvider } from "@/providers/IncidentEditProvider";
import { InvestigationEditProvider } from "@/providers/InvestigationEditProvider";
import { ApplicationEditProvider } from "@/providers/ApplicationEditProvider";
import { BillingItemEditProvider } from "@/providers/BillingItemEditProvider";
import { ExpenseEditProvider } from "@/providers/ExpenseEditProvider";
import { UserOverviewTab } from "@/components/screens/user/UserOverviewTab";
import { UserRelatedTab } from "@/components/screens/user/UserRelatedTab";
import { ProjectOverviewTab } from "@/components/screens/project/ProjectOverviewTab";
import { ProjectRelatedTab } from "@/components/screens/project/ProjectRelatedTab";
import { ProjectTermsAdjudicationTab } from "@/components/screens/project/ProjectTermsAdjudicationTab";
import { ClientOverviewTab } from "@/components/screens/client/ClientOverviewTab";
import { ClientRelatedTab } from "@/components/screens/client/ClientRelatedTab";
import { ClientTermsTab } from "@/components/screens/client/ClientTermsTab";
import { TimecardOverviewTab } from "@/components/screens/timecard/TimecardOverviewTab";
import { ShiftOverviewTab } from "@/components/screens/shift/tabs/ShiftOverviewTab";
// import { ShiftPositionsTab } from "@/components/screens/shift/tabs/ShiftPositionsTab";
import { ShiftPositionsNew as ShiftPositionsTab } from "@/components/screens/shift/tabs/ShiftPositionsNew";
// import { ShiftSupervisorsTab } from "@/components/screens/shift/tabs/ShiftSupervisorsTab";
import { ShiftSupervisorsNew as ShiftSupervisorsTab } from "@/components/screens/shift/tabs/ShiftSupervisorsNew";
import { ShiftRelatedTab } from "@/components/screens/shift/ShiftRelatedTab";
import { ContactOverviewTab } from "@/components/screens/contact/ContactOverviewTab";
import { InvoiceOverviewTab } from "@/components/screens/invoice/InvoiceOverviewTab";
import { EstimateOverviewTab } from "@/components/screens/estimate/EstimateOverviewTab";
import { CourseOverviewTab } from "@/components/screens/course/CourseOverviewTab";
import { IncidentOverviewTab } from "@/components/screens/incident/IncidentOverviewTab";
import { InvestigationOverviewTab } from "@/components/screens/investigation/InvestigationOverviewTab";
import { ExpenseOverviewTab } from "@/components/screens/expense/ExpenseOverviewTab";
import { PayrollOverviewTab } from "@/components/screens/payroll/PayrollOverviewTab";
import { ApplicationOverviewTab } from "@/components/screens/application/ApplicationOverviewTab";
import { BillingItemOverviewTab } from "@/components/screens/billing_item/BillingItemOverviewTab";
import NotFoundScreen from "@/app/+not-found";
import { calculateHoursWorked } from "@/lib/timecard-utils";
import { View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { NavigationHistoryProvider } from "@/contexts/NavigationHistoryContext";
import { CurrentEntityProvider } from "@/contexts/CurrentEntityContext";
import NewIncidentPage, { INCIDENT_STATUS_OPTIONS, getIncidentStatusColor } from "../screens/incident/new";
import NewEstimatePage from "../screens/estimate/new";
import NewInvoicePage from "../screens/invoice/new";
import NewExpensePage from "../screens/expense/new";
import NewInvestigationPage, {
  INVESTIGATION_STATUS_OPTIONS,
  getInvestigationStatusColor,
} from "../screens/investigation/new";
import NewApplicationPage, { APPLICATION_STATUS_OPTIONS, getApplicationStatusColor } from "../screens/application/new";
import NewShiftPage from "../screens/shift/new";
import NewBillingItemPage from "../screens/billing_item/new";
import { SHIFT_STATUS_OPTIONS, getShiftStatusColor } from "@/components/screens/shift/NewShiftForm";
import {
  PROJECT_STATUS_OPTIONS,
  getProjectStatusColor,
  PROJECT_BILLING_STATUS_OPTIONS,
  getProjectBillingStatusColor,
} from "@/components/screens/project/NewProjectForm";
import { CLIENT_STATUS_OPTIONS, getClientStatusColor } from "@/components/screens/client/ClientCreateForm";
import { USER_STATUS_OPTIONS, getUserStatusColor } from "@/components/screens/user/UserCreateForm";
import { ESTIMATE_STATUS_OPTIONS, getEstimateStatusColor } from "@/components/screens/estimate/NewEstimateForm";
import { INVOICE_STATUS_OPTIONS, getInvoiceStatusColor } from "@/components/screens/invoice/NewInvoiceForm";
import { getTimecardStatusOptions } from "@/components/screens/timecard/TimecardCreateForm";
import { COURSE_STATUS_OPTIONS, getCourseStatusColor } from "@/components/screens/course/CourseCreateForm";

export default function DrawerBody() {
  // Create navigator per-instance so it cleans up on unmount (prevents CPU leak)
  // Using useMemo ensures it's only created once per mount, not on every render
  const TopTabs = useMemo(() => createMaterialTopTabNavigator(), []);

  const { colors } = useTheme();
  const { tenantId } = useAuth();
  const segs = useSegments();
  const params = useGlobalSearchParams<{ panel?: string; id?: string; pinned?: string; tab?: string; new?: string }>();
  const [clients, setClients] = useState<ClientType[]>([]);
  const [hoveredTabIndex, setHoveredTabIndex] = useState<number | null>(null);

  // Simple panel detection: params first, then segments fallback
  // Validate against "undefined" string which can occur from stale router params
  const rawPanel = Array.isArray(params.panel) ? params.panel[0] : params.panel || segs[4];
  const panel = rawPanel === "undefined" || !rawPanel ? undefined : rawPanel;
  const rawEntityId = Array.isArray(params.id) ? params.id[0] : params.id;
  const entityId = rawEntityId === "undefined" || !rawEntityId ? undefined : rawEntityId;

  // Use custom hook for entity data
  const { entityData, loading, error, refetch } = useEntityData({
    entityType: panel,
    entityId: entityId,
    enabled: !!(panel && entityId && params.new !== "true"),
  });

  // Navigation ref for programmatic tab navigation
  const navigationRef = useRef<any>(null);
  const prevEntityRef = useRef<string | null>(null);

  // Navigate to correct tab when entity changes
  useEffect(() => {
    // Skip if panel or entityId is invalid
    if (!panel || !entityId) return;

    const currentEntity = `${panel}-${entityId}`;
    if (prevEntityRef.current !== currentEntity && navigationRef.current?.isReady()) {
      const targetTab = params.tab || "overview";
      // Validate tab is not "undefined" string
      if (targetTab === "undefined" || !targetTab) return;
      const tabName = targetTab.charAt(0).toUpperCase() + targetTab.slice(1);
      // Validate tab name is a known valid tab before navigating
      const validTabs = [
        "Overview",
        "Related",
        "Meta",
        "Activity",
        "Files",
        "Positions",
        "Supervisors",
        "Incidents",
        "Terms",
      ];
      if (validTabs.includes(tabName)) {
        navigationRef.current?.navigate(tabName);
      }
      prevEntityRef.current = currentEntity;
    }
  }, [panel, entityId, params.tab]);

  // Fetch clients data for contacts
  useEffect(() => {
    const fetchMetaData = async () => {
      if (panel === "contact") {
        try {
          const clientsData = await clientService.getClients();
          setClients(clientsData);
        } catch (err) {
          throw Error("Failed to fetch", err);
        }
      }
    };
    fetchMetaData();
  }, [tenantId, panel]);

  // For creating new entities
  let newScreen: React.ReactNode;
  if (params.new === "true") {
    switch (panel) {
      case "user":
        newScreen = <NewUser />;
        break;
      case "project":
        newScreen = <NewProjectPage />;
        break;
      case "client":
        newScreen = <NewClient />;
        break;
      case "contact":
        newScreen = <NewContact />;
        break;
      case "time_card":
        newScreen = <NewTimecard />;
        break;
      case "course":
        newScreen = <NewCoursePage />;
        break;
      case "incident":
        newScreen = <NewIncidentPage />;
        break;
      case "estimate":
        newScreen = <NewEstimatePage />;
        break;
      case "invoice":
        newScreen = <NewInvoicePage />;
        break;
      case "expense":
        newScreen = <NewExpensePage />;
        break;
      case "investigation":
        newScreen = <NewInvestigationPage />;
        break;
      case "application":
        newScreen = <NewApplicationPage />;
        break;
      case "shift":
        newScreen = <NewShiftPage />;
        break;
      case "billing_item":
        newScreen = <NewBillingItemPage />;
        break;
      case "payroll":
        newScreen = (
          <View style={{ padding: 16 }}>
            <ThemedText>New {panel} (Placeholder)</ThemedText>
          </View>
        );
        break;
      default:
        newScreen = (
          <View>
            <ThemedText>No create screen for: {panel}</ThemedText>
          </View>
        );
    }
  }

  // Context-wrapped tab components with edit providers

  const renderEntityTabs = () => {
    // if (loading) {
    //   return <TopTabs.Screen name="Overview" children={() => <ThemedText>Loading...</ThemedText>} />;
    // }

    switch (panel) {
      case "user":
        return (
          <>
            <TopTabs.Screen name="Overview" children={() => <UserOverviewTab user={entityData} loading={loading} />} />
            <TopTabs.Screen name="Related" children={() => <UserRelatedTab user={entityData} loading={loading} />} />
            <TopTabs.Screen
              name="Comments"
              children={() => <Comments entityType="users" entityId={entityId} loading={loading} />}
            />
            <TopTabs.Screen
              name="Files"
              children={() => <FilesTab entityType="user" entityId={entityId} loading={loading} />}
            />
          </>
        );

      case "project":
        return (
          <>
            <TopTabs.Screen
              name="Overview"
              children={() => <ProjectOverviewTab project={entityData} loading={loading} />}
            />
            <TopTabs.Screen
              name="Related"
              children={() => <ProjectRelatedTab project={entityData} loading={loading} />}
            />
            <TopTabs.Screen
              name="Terms"
              children={() => <ProjectTermsAdjudicationTab project={entityData} loading={loading} />}
            />
            <TopTabs.Screen
              name="Comments"
              children={() => <Comments entityType="projects" entityId={entityId} loading={loading} />}
            />
            <TopTabs.Screen
              name="Files"
              children={() => <FilesTab entityType="project" entityId={entityId} loading={loading} />}
            />
          </>
        );

      case "client":
        return (
          <>
            <TopTabs.Screen
              name="Overview"
              children={() => <ClientOverviewTab client={entityData} loading={loading} />}
            />
            <TopTabs.Screen
              name="Related"
              children={() => <ClientRelatedTab client={entityData} loading={loading} />}
            />
            <TopTabs.Screen name="Terms" children={() => <ClientTermsTab client={entityData} loading={loading} />} />
            <TopTabs.Screen
              name="Comments"
              children={() => <Comments entityType="clients" entityId={entityId} loading={loading} />}
            />
            <TopTabs.Screen
              name="Files"
              children={() => <FilesTab entityType="client" entityId={entityId} loading={loading} />}
            />
          </>
        );

      case "time_card":
        return (
          <>
            <TopTabs.Screen
              name="Overview"
              children={() => <TimecardOverviewTab timecard={entityData} loading={loading} />}
            />
          </>
        );
      case "shift":
        return (
          <>
            <TopTabs.Screen
              name="Overview"
              children={() => <ShiftOverviewTab shift={entityData} loading={loading} />}
            />
            <TopTabs.Screen
              name="Positions"
              children={() => <ShiftPositionsTab shift={entityData} loading={loading} />}
            />
            <TopTabs.Screen
              name="Supervisors"
              children={() => <ShiftSupervisorsTab shift={entityData} loading={loading} />}
            />
            <TopTabs.Screen name="Related" children={() => <ShiftRelatedTab shift={entityData} loading={loading} />} />
            <TopTabs.Screen
              name="Comments"
              children={() => <Comments entityType="shifts" entityId={entityId} loading={loading} />}
            />
            <TopTabs.Screen
              name="Files"
              children={() => <FilesTab entityType="shift" entityId={entityId} loading={loading} />}
            />
          </>
        );
      case "contact":
        return (
          <>
            <TopTabs.Screen
              name="Overview"
              children={() => <ContactOverviewTab contact={entityData} loading={loading} />}
            />
            <TopTabs.Screen
              name="Comments"
              children={() => <Comments entityType="contacts" entityId={entityId} loading={loading} />}
            />
            <TopTabs.Screen
              name="Files"
              children={() => <FilesTab entityType="contact" entityId={entityId} loading={loading} />}
            />
          </>
        );
      case "invoice":
        return (
          <>
            <TopTabs.Screen
              name="Overview"
              children={() => <InvoiceOverviewTab invoice={entityData} loading={loading} />}
            />
            <TopTabs.Screen
              name="Files"
              children={() => <FilesTab entityType="invoice" entityId={entityId} loading={loading} />}
            />
          </>
        );
      case "estimate":
        return (
          <>
            <TopTabs.Screen
              name="Overview"
              children={() => <EstimateOverviewTab estimate={entityData} loading={loading} />}
            />
            <TopTabs.Screen
              name="Files"
              children={() => <FilesTab entityType="estimate" entityId={entityId} loading={loading} />}
            />
          </>
        );
      case "course":
        return (
          <>
            <TopTabs.Screen
              name="Overview"
              children={() => <CourseOverviewTab course={entityData} loading={loading} />}
            />
            <TopTabs.Screen
              name="Files"
              children={() => <FilesTab entityType="course" entityId={entityId} loading={loading} />}
            />
          </>
        );
      case "incident":
        return (
          <>
            <TopTabs.Screen
              name="Overview"
              children={() => <IncidentOverviewTab incident={entityData} loading={loading} />}
            />
            <TopTabs.Screen
              name="Comments"
              children={() => <Comments entityType="incidents" entityId={entityId} loading={loading} />}
            />
            <TopTabs.Screen
              name="Files"
              children={() => <FilesTab entityType="incident" entityId={entityId} loading={loading} />}
            />
          </>
        );
      case "investigation":
        return (
          <>
            <TopTabs.Screen
              name="Overview"
              children={() => <InvestigationOverviewTab investigation={entityData} loading={loading} />}
            />
            <TopTabs.Screen
              name="Files"
              children={() => <FilesTab entityType="investigation" entityId={entityId} loading={loading} />}
            />
          </>
        );
      case "expense":
        return (
          <>
            <TopTabs.Screen
              name="Overview"
              children={() => <ExpenseOverviewTab expense={entityData} loading={loading} />}
            />
            <TopTabs.Screen
              name="Files"
              children={() => <FilesTab entityType="expense" entityId={entityId} loading={loading} />}
            />
          </>
        );
      case "payroll":
        return (
          <>
            <TopTabs.Screen
              name="Overview"
              children={() => <PayrollOverviewTab payroll={entityData} loading={loading} />}
            />
            <TopTabs.Screen
              name="Files"
              children={() => <FilesTab entityType="payroll" entityId={entityId} loading={loading} />}
            />
          </>
        );
      case "application":
        return (
          <>
            <TopTabs.Screen
              name="Overview"
              children={() => <ApplicationOverviewTab application={entityData} loading={loading} />}
            />
          </>
        );
      case "billing_item":
        return (
          <>
            <TopTabs.Screen
              name="Overview"
              children={() => <BillingItemOverviewTab billingItem={entityData} loading={loading} />}
            />
            <TopTabs.Screen
              name="Files"
              children={() => <FilesTab entityType="billing_item" entityId={entityId} loading={loading} />}
            />
          </>
        );
      default:
        return <TopTabs.Screen name="Overview" children={() => <NotFoundScreen />} />;
    }
  };

  // Validate if requested tab exists for current entity type
  const getValidInitialRoute = (entityType: string, requestedTab: string): string => {
    const tabsByEntity: { [key: string]: string[] } = {
      user: ["Overview", "Emergency Contacts", "Related", "Comments", "Files", "Incidents"],
      project: ["Overview", "Related", "Terms", "Comments", "Files"],
      client: ["Overview", "Related", "Banned Users", "Terms", "Comments", "Files"],
      time_card: ["Overview", "Files"],
      shift: ["Overview", "Positions", "Supervisors", "Related", "Comments", "Files"],
      contact: ["Overview", "Comments", "Files"],
      invoice: ["Overview", "Files"],
      estimate: ["Overview", "Files"],
      course: ["Overview", "Files"],
      incident: ["Overview", "Comments", "Files"],
      investigation: ["Overview", "Files"],
      expense: ["Overview", "Files"],
      payroll: ["Overview", "Files"],
      application: ["Overview"],
      billing_item: ["Overview", "Files"],
    };

    const availableTabs = tabsByEntity[entityType] || ["Overview"];
    const capitalizedTab = titleCase(requestedTab || "overview");

    return availableTabs.includes(capitalizedTab) ? capitalizedTab : "Overview";
  };

  // Get status options for each entity type
  const getStatusColumns = (entityType: string | undefined): StatusColumn[] => {
    const cols: StatusColumn[] = [];

    // Primary status
    const statusOpts = (() => {
      switch (entityType) {
        case "shift":
          return SHIFT_STATUS_OPTIONS.map((opt) => ({
            text: opt.text,
            bgColor: getShiftStatusColor(opt.text, colors),
          }));
        case "project":
          return PROJECT_STATUS_OPTIONS.map((opt) => ({
            text: opt.text,
            bgColor: getProjectStatusColor(opt.text, colors),
          }));
        case "client":
          return CLIENT_STATUS_OPTIONS.map((opt) => ({
            text: opt.text,
            bgColor: getClientStatusColor(opt.text, colors),
          }));
        case "user":
          return USER_STATUS_OPTIONS.map((opt) => ({ text: opt.text, bgColor: getUserStatusColor(opt.text, colors) }));
        case "estimate":
          return ESTIMATE_STATUS_OPTIONS.map((opt) => ({
            text: opt.text,
            bgColor: getEstimateStatusColor(opt.text, colors),
          }));
        case "invoice":
          return INVOICE_STATUS_OPTIONS.map((opt) => ({
            text: opt.text,
            bgColor: getInvoiceStatusColor(opt.text, colors),
          }));
        case "time_card":
          return getTimecardStatusOptions(colors);
        case "application":
          return APPLICATION_STATUS_OPTIONS.map((opt) => ({
            text: opt.text,
            bgColor: getApplicationStatusColor(opt.text, colors),
          }));
        case "incident":
          return INCIDENT_STATUS_OPTIONS.map((opt) => ({
            text: opt.text,
            bgColor: getIncidentStatusColor(opt.text, colors),
          }));
        case "investigation":
          return INVESTIGATION_STATUS_OPTIONS.map((opt) => ({
            text: opt.text,
            bgColor: getInvestigationStatusColor(opt.text, colors),
          }));
        case "course":
          return COURSE_STATUS_OPTIONS.map((opt) => ({
            text: opt.text,
            bgColor: getCourseStatusColor(opt.text, colors),
          }));
        default:
          return undefined;
      }
    })();

    if (statusOpts) {
      cols.push({ label: "Status", key: "status", options: statusOpts, placeholder: "Select status" });
    }

    // Secondary status columns per entity
    if (entityType === "project") {
      cols.push({
        label: "Billing Status",
        key: "billing_status",
        options: PROJECT_BILLING_STATUS_OPTIONS.map((opt) => ({
          text: opt.text,
          bgColor: getProjectBillingStatusColor(opt.text, colors),
        })),
        placeholder: "Billing status",
        inMetaData: true,
      });
    }

    return cols;
  };

  const renderWithProvider = () => {
    const tabContent = (
      <TopTabs.Navigator
        layout={({ children }) => (
          <CurrentEntityProvider title={getEntityTitle()} panel={panel} id={entityId}>
            {params.new === "true" ? null : (
              <PreviewHeader
                title={getEntityTitle()}
                subtitle={getEntitySubtitle()}
                entityType={panel}
                entityId={entityId}
                editableName={panel === "user" || panel === "contact"}
                editableSingleName={
                  panel === "project" ||
                  panel === "shift" ||
                  panel === "incident" ||
                  panel === "investigation" ||
                  panel === "client"
                }
                editableDescription={panel === "shift" || panel === "client"}
                avatar={
                  panel === "user"
                    ? {
                        userId: entityId,
                        email: entityData?.email_address,
                        firstName: entityData?.first_name,
                        lastName: entityData?.last_name,
                        size: 60,
                      }
                    : panel === "contact"
                      ? {
                          userId: entityId,
                          email: entityData?.email_address,
                          firstName: entityData?.first_name,
                          lastName: entityData?.last_name,
                          size: 60,
                        }
                      : undefined
                }
                logo={
                  panel === "client"
                    ? {
                        clientId: entityId,
                        clientName: entityData?.name,
                        height: 60,
                      }
                    : undefined
                }
                statusColumns={getStatusColumns(panel)}
              />
            )}
            {children}
            {params.new === "true" ? null : (
              <PreviewFooter
                createdAt={entityData?.created_at}
                updatedAt={entityData?.updated_at}
                createdBy={entityData?.created_by}
                updatedBy={entityData?.updated_by}
              />
            )}
          </CurrentEntityProvider>
        )}
        screenListeners={({ route }) => ({
          tabPress: () => {
            router.setParams({ tab: route.name.toLowerCase() });
          },
        })}
        initialRouteName={getValidInitialRoute(panel || "", params.tab || "overview")}
        tabBar={(props) => (
          <DrawerTabBar
            {...props}
            onTabHoverIn={(_route, index) => setHoveredTabIndex(index)}
            onTabHoverOut={() => setHoveredTabIndex(null)}
            hoveredIndex={hoveredTabIndex}
            tabBarHoverStyle={{ backgroundColor: colors.backgroundSecondary }}
          />
        )}
        //screenLayout={({ children }) => (
        //  <View style={{ width: "100%", height: "100%", overflow: "scroll" }}>{children}</View>
        //)}
        screenOptions={({ route }) => ({
          animationEnabled: false,
          lazy: true,
          lazyPreloadDistance: 0,
          swipeEnabled: false,
          tabBarIndicatorContainerStyle: { marginLeft: 4 },
          tabBarStyle: {
            paddingLeft: 4,
            backgroundColor: colors.background,
            justifyContent: "flex-start",
            // paddingHorizontal: 2,
          },
          tabBarItemStyle: { width: "auto" },
          // tabBarLabelStyle: { color: colors.text },
          sceneStyle: { backgroundColor: colors.backgroundSecondary },
          tabBarShowIcon: true,
          tabBarIcon: () => {
            const iconMap: Record<string, string> = {
              Overview: "information-outline",
              Related: "link-variant",
              Comments: "comment-outline",
              Files: "folder-outline",
              Terms: "file-document-outline",
              Positions: "account-group-outline",
              Supervisors: "shield-account-outline",
            };
            const iconName = iconMap[route.name] ?? "circle-outline";
            return <MaterialCommunityIcons name={iconName as any} size={16} color={colors.text} />;
          },
        })}
      >
        {renderEntityTabs()}
      </TopTabs.Navigator>
    );

    // Wrap with appropriate context provider
    switch (panel) {
      case "user":
        return (
          <UserEditProvider entityData={entityData} loading={loading} onSaveSuccess={refetch}>
            {tabContent}
          </UserEditProvider>
        );
      case "project":
        return (
          <ProjectEditProvider entityData={entityData} loading={loading} onSaveSuccess={refetch}>
            {tabContent}
          </ProjectEditProvider>
        );
      case "client":
        return (
          <ClientEditProvider entityData={entityData} loading={loading} onSaveSuccess={refetch}>
            {tabContent}
          </ClientEditProvider>
        );
      case "time_card":
        return (
          <TimecardEditProvider entityData={entityData} loading={loading} onSaveSuccess={refetch}>
            {tabContent}
          </TimecardEditProvider>
        );
      case "shift":
        return (
          <ShiftEditProvider entityData={entityData} loading={loading} onSaveSuccess={refetch}>
            {tabContent}
          </ShiftEditProvider>
        );
      case "contact":
        return (
          <ContactEditProvider entityData={entityData} loading={loading} onSaveSuccess={refetch}>
            {tabContent}
          </ContactEditProvider>
        );
      case "invoice":
        return (
          <InvoiceEditProvider entityData={entityData} loading={loading} onSaveSuccess={refetch}>
            {tabContent}
          </InvoiceEditProvider>
        );
      case "estimate":
        return (
          <EstimateEditProvider entityData={entityData} loading={loading} onSaveSuccess={refetch}>
            {tabContent}
          </EstimateEditProvider>
        );
      case "course":
        return (
          <CourseEditProvider entityData={entityData} loading={loading} onSaveSuccess={refetch}>
            {tabContent}
          </CourseEditProvider>
        );
      case "incident":
        return (
          <IncidentEditProvider entityData={entityData} loading={loading} onSaveSuccess={refetch}>
            {tabContent}
          </IncidentEditProvider>
        );
      case "investigation":
        return (
          <InvestigationEditProvider entityData={entityData} loading={loading} onSaveSuccess={refetch}>
            {tabContent}
          </InvestigationEditProvider>
        );
      case "application":
        return (
          <ApplicationEditProvider entityData={entityData} loading={loading} onSaveSuccess={refetch}>
            {tabContent}
          </ApplicationEditProvider>
        );
      case "billing_item":
        return (
          <BillingItemEditProvider entityData={entityData} loading={loading} onSaveSuccess={refetch}>
            {tabContent}
          </BillingItemEditProvider>
        );
      case "expense":
        return (
          <ExpenseEditProvider entityData={entityData} loading={loading} onSaveSuccess={refetch}>
            {tabContent}
          </ExpenseEditProvider>
        );
      default:
        return tabContent;
    }
  };

  // Get the entity title for PreviewHeader
  const getEntityTitle = () => {
    if (!entityData) return panel || "Entity";

    switch (panel) {
      case "project":
        return entityData.name || "Project";
      case "client":
        return entityData.name || "Client";
      case "user":
        return `${entityData.first_name} ${entityData.last_name}`.trim() || "User";
      case "shift":
        return entityData.name || entityData.description || entityData.project?.name || "Unnamed Shift";
      case "time_card":
        // return `Timecard ${entityData.user_id}`;
        return `Timecard`;
      case "contact":
        return `${entityData.first_name} ${entityData.last_name}`.trim() || "Contact";
      case "invoice":
        return entityData.meta_data?.invoice_number || `Invoice ${entityData.id}`;
      case "estimate":
        return entityData.meta_data?.estimate_number || `Estimate ${entityData.id}`;
      case "course":
        return entityData.name || "Course";
      case "incident":
        return entityData.name || `Incident ${entityData.id?.toString().slice(0, 6).toUpperCase()}`;
      case "investigation":
        return entityData.name || `Investigation ${entityData.id?.toString().slice(0, 6).toUpperCase()}`;
      case "expense":
        return `Expense ${entityData.id?.toString().slice(0, 6).toUpperCase()}`;
      case "payroll":
        return `Payroll ${entityData.id?.toString().slice(0, 6).toUpperCase()}`;
      case "application":
        return entityData.applicant_name || "Application";
      case "billing_item":
        return `Billing Item ${entityData.id?.toString().slice(0, 6).toUpperCase() || ""}`;
      default:
        return panel || "Entity";
    }
  };

  const getEntitySubtitle = () => {
    if (!entityData) return undefined;

    switch (panel) {
      case "project":
        return entityData.description;
      case "client":
        return entityData.description;
      case "user":
        return entityData.job_title;
      case "shift":
        return entityData.description || "Shift Details";
      case "time_card":
        const hoursWorked = calculateHoursWorked(
          entityData.start_time,
          entityData.end_time,
          entityData.meal_start,
          entityData.meal_end,
        );
        return `${hoursWorked.toFixed(2)} hours worked`;
      case "contact":
        return entityData.meta_data?.preferred_name || entityData.email_address || "Contact Details";
      case "invoice":
        const invoiceStatus = entityData.meta_data?.status || "draft";
        const invoiceAmount = entityData.meta_data?.amount_due;
        return invoiceAmount
          ? `${invoiceStatus.toUpperCase()} • $${invoiceAmount.toFixed(2)}`
          : invoiceStatus.toUpperCase();
      case "estimate":
        const estimateStatus = entityData.meta_data?.status || "draft";
        const estimateAmount = entityData.meta_data?.amount_due;
        const expiryDate = entityData.meta_data?.expiry_date;
        let subtitle = estimateAmount
          ? `${estimateStatus.toUpperCase()} • $${estimateAmount.toFixed(2)}`
          : estimateStatus.toUpperCase();
        if (expiryDate && new Date(expiryDate) < new Date() && estimateStatus !== "expired") {
          subtitle += " • EXPIRED";
        }
        return subtitle;
      case "course":
        return entityData.description || entityData.status?.toUpperCase();
      case "incident":
        return entityData.description || entityData.status?.toUpperCase();
      case "investigation":
        return entityData.description || entityData.status?.toUpperCase();
      case "expense":
        const expenseAmount = entityData.amount;
        return expenseAmount ? `$${Number(expenseAmount).toFixed(2)}` : entityData.status?.toUpperCase();
      case "payroll":
        const payrollAmount = entityData.amount;
        const period =
          entityData.period_start && entityData.period_end
            ? `${new Date(entityData.period_start).toLocaleDateString()} - ${new Date(entityData.period_end).toLocaleDateString()}`
            : entityData.status?.toUpperCase();
        return payrollAmount ? `$${Number(payrollAmount).toFixed(2)} • ${period}` : period;
      case "application":
        return entityData.position_applied
          ? `${entityData.status?.toUpperCase()} • ${entityData.position_applied}`
          : entityData.status?.toUpperCase();
      case "billing_item":
        const billingAmount = (entityData.meta_data as any)?.amount;
        const projectName = entityData.project?.name;
        return billingAmount
          ? `$${Number(billingAmount).toFixed(2)}${projectName ? ` • ${projectName}` : ""}`
          : projectName || undefined;
      default:
        return undefined;
    }
  };

  return (
    <NavigationHistoryProvider>
      <NavigationIndependentTree>
        <NavigationContainer ref={navigationRef} documentTitle={{ enabled: false }}>
          {params.new === "true" ? <View style={{ flex: 1 }}>{newScreen}</View> : renderWithProvider()}
        </NavigationContainer>
      </NavigationIndependentTree>
    </NavigationHistoryProvider>
  );
}
